/**
 * Carril horizontal de las secciones "kit-" (snippets/kit-scroller.liquid).
 * - Barra de progreso que sigue el desplazamiento y se oculta si todo cabe.
 * - Arrastre con ratón en escritorio (en táctil se usa el scroll nativo).
 * - Flechas opcionales que avanzan una tarjeta y se desactivan en los extremos.
 */
class KitScroller extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;

  /** @type {HTMLElement | null} */
  #progress = null;

  /** @type {HTMLElement | null} */
  #arrows = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  #drag = { active: false, moved: false, startX: 0, startScroll: 0 };

  connectedCallback() {
    this.#track = this.querySelector('.kit-scroller__track');
    this.#progress = this.querySelector('.kit-scroller__progress');
    this.#arrows = this.querySelector('.kit-scroller__arrows');
    if (!this.#track) return;

    this.#arrows?.addEventListener('click', this.#onArrowClick);

    this.#track.addEventListener('scroll', this.#update, { passive: true });
    this.#track.addEventListener('pointerdown', this.#onPointerDown);
    this.#track.addEventListener('click', this.#onClickCapture, true);

    this.#resizeObserver = new ResizeObserver(this.#update);
    this.#resizeObserver.observe(this.#track);
    this.#update();
  }

  disconnectedCallback() {
    this.#arrows?.removeEventListener('click', this.#onArrowClick);
    this.#track?.removeEventListener('scroll', this.#update);
    this.#track?.removeEventListener('pointerdown', this.#onPointerDown);
    this.#track?.removeEventListener('click', this.#onClickCapture, true);
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#endDrag();
  }

  #update = () => {
    const track = this.#track;
    if (!track) return;

    const maxScroll = track.scrollWidth - track.clientWidth;
    const position = Math.abs(track.scrollLeft);

    if (this.#arrows) {
      this.#arrows.hidden = maxScroll <= 1;
      const [prev, next] = this.#arrows.querySelectorAll('button');
      if (prev instanceof HTMLButtonElement) prev.disabled = position <= 2;
      if (next instanceof HTMLButtonElement) next.disabled = position >= maxScroll - 2;
    }

    const progress = this.#progress;
    if (!progress) return;

    progress.hidden = maxScroll <= 1;
    if (progress.hidden) return;

    const size = (track.clientWidth / track.scrollWidth) * 100;
    const ratio = Math.min(Math.max(Math.abs(track.scrollLeft) / maxScroll, 0), 1);
    progress.style.setProperty('--kit-thumb-size', `${size}%`);
    progress.style.setProperty('--kit-thumb-offset', `${ratio * ((100 - size) / size) * 100}%`);
  };

  /** @param {MouseEvent} event */
  #onArrowClick = (event) => {
    const track = this.#track;
    const arrow = event.target instanceof Element ? event.target.closest('.kit-scroller__arrow') : null;
    const item = track?.firstElementChild;
    if (!track || !(arrow instanceof HTMLButtonElement) || !(item instanceof HTMLElement)) return;

    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    track.scrollBy({ left: Number(arrow.dataset.direction) * (item.offsetWidth + gap), behavior: 'smooth' });
  };

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    const track = this.#track;
    if (!track || event.pointerType !== 'mouse' || event.button !== 0) return;
    if (track.scrollWidth <= track.clientWidth) return;

    this.#drag = { active: true, moved: false, startX: event.clientX, startScroll: track.scrollLeft };
    window.addEventListener('pointermove', this.#onPointerMove);
    window.addEventListener('pointerup', this.#endDrag);
  };

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    const track = this.#track;
    if (!track || !this.#drag.active) return;

    const delta = event.clientX - this.#drag.startX;
    if (!this.#drag.moved && Math.abs(delta) < 5) return;

    this.#drag.moved = true;
    track.classList.add('is-dragging');
    track.scrollLeft = this.#drag.startScroll - delta;
  };

  #endDrag = () => {
    window.removeEventListener('pointermove', this.#onPointerMove);
    window.removeEventListener('pointerup', this.#endDrag);
    this.#drag.active = false;
    this.#track?.classList.remove('is-dragging');
  };

  /** Evita que soltar tras arrastrar dispare un clic en el contenido. */
  /** @param {MouseEvent} event */
  #onClickCapture = (event) => {
    if (!this.#drag.moved) return;
    event.preventDefault();
    event.stopPropagation();
    this.#drag.moved = false;
  };
}

if (!customElements.get('kit-scroller')) {
  customElements.define('kit-scroller', KitScroller);
}
